import { map, isEmpty, isEqual } from 'lodash/fp';
import { createAction, DateTimeUtcService, lt, showErrorAlert } from '@mews/framework';
import { createUUID } from '@mews/std';
import { MessageThreadType, TextKey } from '@mews-commander/enums';
import { CommanderThunkAction } from '@mews-commander/types';
import { isFalse } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { setThread } from '@mews-commander/legacy-entities/src/ducks/threads';
import { THREADS_COUNT_TO_FETCH } from '../constants';
import {
    getCurrentThreadId,
    currentThreadSubject,
    isCurrentThreadDraft,
    getCurrentThreadType,
    getLatestVisibleThreadId,
    isCurrentThreadRead,
    getCurrentUserData,
    isThreadPosted,
} from '../selectors/threads';
import { threadSubjectDraft } from '../selectors';
import { getStubMessage } from '../selectors/utils';
import {
    ALL_THREADS_LOADED,
    THREAD_SELECTED,
    THREAD_SUBJECT_EDITING,
    THREAD_SUBJECT_DRAFT_UPDATED,
    THREAD_POSTED,
    VIEW_MODE_SET,
    THREAD_DRAFT_CREATED,
    THREAD_DRAFT_REMOVED,
    THREAD_DRAFT_UPDATED,
} from '../actionTypes';
import { fetchThreadsList, fetchSingleThread, markThreadRead, updateThreadSubject } from './threadsApi';
import { memberAdded } from './members';
import { addStubMessage, updateMessage, removeMessage } from './messages';
import { fetchCurrentEnterpriseProfileWithCheck } from './profiles';

export function fetchThreadData(threadId: $TSFixMe) {
    return (dispatch: $TSFixMe, getState: $TSFixMe) =>
        dispatch(fetchSingleThread(threadId)).then((data: $TSFixMe) => {
            // check if the current thread was updated
            const currentThreadId = getCurrentThreadId(getState());
            if (isEqual(currentThreadId, threadId) && isFalse(isCurrentThreadRead(getState()))) {
                dispatch(markThreadRead(threadId));
            }
            return data;
        });
}

export function fetchThreads({ threadType, startIndex = 0, count = THREADS_COUNT_TO_FETCH }: $TSFixMe) {
    return (dispatch: $TSFixMe) =>
        dispatch(fetchThreadsList({ startIndex, count, threadType })).then(({ value }: $TSFixMe) => {
            const { messageThreads } = value;
            if (isEmpty(messageThreads)) {
                dispatch(fetchCurrentEnterpriseProfileWithCheck());
                dispatch(createAction(ALL_THREADS_LOADED, threadType));
            }
        });
}

export const selectThread = ({ threadId }: $TSFixMe) => createAction(THREAD_SELECTED, threadId);

export function closeCurrentThread() {
    return (dispatch: $TSFixMe) => {
        dispatch(currentThreadUnselected());
        dispatch(selectThread({ threadId: null }));
    };
}

export const createThreadDraft =
    (): CommanderThunkAction =>
    async (dispatch, getState, { container, appClockService }) => {
        const state = getState();
        const { appLocalizationComponent } = container();

        const threadId = createUUID();
        const thread = {
            id: threadId,
            isRead: true,
            updatedUtc: DateTimeUtcService.toISOString(appClockService.getAppNowUtc()),
            subject: appLocalizationComponent.localizeText(TextKey.ThreadNew),
            isDraft: true,
            isStub: false,
        };
        const currentUser = getCurrentUserData(state);

        dispatch(createAction(THREAD_DRAFT_CREATED, thread));

        dispatch(memberAdded(threadId, { ...currentUser, isEmployee: true, isActive: true }));
        return dispatch(selectThread({ threadId }));
    };

export const addThread =
    (thread: $TSFixMe, text: $TSFixMe, members: $TSFixMe): CommanderThunkAction =>
    (dispatch, getState, { appClockService }) => {
        const stubMessage = getStubMessage(getState());
        const messageId = createUUID();

        const createdUtc = appClockService.getAppNowUtc().value;

        const message = { ...stubMessage, id: messageId, text, createdUtc };

        dispatch(createAction(THREAD_DRAFT_UPDATED, { id: thread.id, isDraft: false, isStub: true }));
        dispatch(addStubMessage(thread.id, message));
        return dispatch(postThread(thread, message, members));
    };

export function postThread(thread: $TSFixMe, message: $TSFixMe, members: $TSFixMe) {
    return (dispatch: $TSFixMe, getState: $TSFixMe, { container }: { container: () => CommanderContainer }) => {
        const { messageThreadComponent } = container();
        const memberProfileIds = map('profileId')(members);
        const enterpriseId = getCurrentEnterpriseId();
        return messageThreadComponent
            .addThread({ subject: thread.subject, text: message.text, memberProfileIds, enterpriseId })
            .catch((error: $TSFixMe) => {
                const description = error?.message ? <>{error.message}</> : lt('MessageSendFailedErrorMessage');
                showErrorAlert({ description });
                dispatch(updateMessage(thread.id, message.id, { isFailed: true }));
                throw error;
            })
            .then((result: $TSFixMe) => {
                const currentThreadId = getCurrentThreadId(getState());
                dispatch(threadPosted(thread.id, result));
                if (currentThreadId === thread.id) {
                    dispatch(selectThreadAndFetchData({ threadId: result.id })).then(() => {
                        dispatch(removeMessage(thread.id, message.id));
                        dispatch(removeMessage(result.id, message.id));
                    });
                }
            });
    };
}

export function enterSubjectEditMode() {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const currentSubject = currentThreadSubject(getState());
        dispatch(createAction(THREAD_SUBJECT_DRAFT_UPDATED, currentSubject));
        return dispatch(createAction(THREAD_SUBJECT_EDITING, true));
    };
}

export function closeSubjectEditMode() {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const currentSubject = currentThreadSubject(getState());
        dispatch(createAction(THREAD_SUBJECT_DRAFT_UPDATED, currentSubject));
        return dispatch(createAction(THREAD_SUBJECT_EDITING, false));
    };
}

export function saveThreadSubject(threadId: $TSFixMe) {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const subjectDraft = threadSubjectDraft(getState());
        const currentSubject = currentThreadSubject(getState());

        dispatch(createAction(THREAD_SUBJECT_EDITING, false));
        const threadPosted = isThreadPosted(getState(), { id: threadId });

        dispatch(createAction(THREAD_DRAFT_UPDATED, { id: threadId, subject: subjectDraft }));

        if (threadPosted) {
            dispatch(updateThreadSubject({ subjectDraft, threadId })).catch((error: $TSFixMe) => {
                const description = error?.message ? <>{error.message}</> : lt('SubjectUpdatingErrorMessage');
                showErrorAlert({ description });
                dispatch(createAction(THREAD_DRAFT_UPDATED, { id: threadId, subject: currentSubject }));
                throw error;
            });
        }
    };
}

export const removeThreadDraft = (threadId: $TSFixMe) => createAction(THREAD_DRAFT_REMOVED, { id: threadId });

export const threadPosted = (oldThreadId: $TSFixMe, newThread: $TSFixMe) => (dispatch: $TSFixMe) => {
    dispatch(setThread(newThread));
    dispatch(removeThreadDraft(oldThreadId));
    return dispatch(createAction(THREAD_POSTED, { oldThreadId, newThreadId: newThread.id }));
};

function currentThreadUnselected() {
    return (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();
        if (isCurrentThreadDraft(state)) {
            const threadId = getCurrentThreadId(state);
            dispatch(removeThreadDraft(threadId));
        }
    };
}

const setViewMode = ({ viewMode }: $TSFixMe) => createAction(VIEW_MODE_SET, viewMode);

export const selectThreadAndFetchData =
    ({ threadId }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        dispatch(selectThread({ threadId }));

        if (!threadId) {
            return;
        }

        if (isFalse(isCurrentThreadRead(getState()))) {
            dispatch(markThreadRead(threadId));
        }
        return dispatch(fetchThreadData(threadId));
    };

export const messagingModuleInit =
    ({ threadId }: $TSFixMe) =>
    async (dispatch: $TSFixMe, getState: $TSFixMe) => {
        if (threadId) {
            await dispatch(selectThreadAndFetchData({ threadId }));

            const viewMode = getCurrentThreadType(getState());
            dispatch(setViewMode({ viewMode }));

            return dispatch(fetchThreads({ threadType: viewMode }));
        }

        const viewMode = MessageThreadType.Private;
        await dispatch(fetchThreads({ threadType: viewMode }));

        dispatch(setViewMode({ viewMode }));

        return dispatch(selectLatestVisibleThread());
    };

export const switchViewMode =
    ({ viewMode }: $TSFixMe) =>
    async (dispatch: $TSFixMe) => {
        dispatch(selectThread({ threadId: null }));
        dispatch(setViewMode({ viewMode }));
        await dispatch(fetchThreads({ threadType: viewMode }));

        return dispatch(selectLatestVisibleThread());
    };

const selectLatestVisibleThread = () => (dispatch: $TSFixMe, getState: $TSFixMe) => {
    // @ts-expect-error $TSFixMeError: Expected 0 arguments, but got 1.
    const latestThreadId = getLatestVisibleThreadId(getState());
    return dispatch(selectThreadAndFetchData({ threadId: latestThreadId }));
};
